import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import moment from 'moment';

import RightModalLayout from '../../../../layouts/Modal/RightModalLayout';
import ModalSection from '../ModalSection';
import { SIDEBAR_ACTIONS } from '../../../../../constants/actionsConstants';
import HistoryModel from '../../../../models/HistoryModel';

import 'react-quill/dist/quill.snow.css';

const HistoryDetailModal = ({ historyRecord }) => {
  const { t } = useTranslation('ChatbotIA');
  const dispatch = useDispatch();
  const [fullRecord, setFullRecord] = useState(null);

  useEffect(() => {
    const loadFullRecord = async () => {
      const response = await HistoryModel.findById(historyRecord.id);
      setFullRecord(response.data);
    };

    loadFullRecord();
  }, [historyRecord]);

  const closeModal = () => {
    dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
  };

  const translateEvent = (event) => {
    return t(`config.history.events.${event.replace(/ /g, '_')}`);
  };

  const formatValue = (value) => {
    if (!value) return null;
    try {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value;
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return value;
    }
  };

  const stripHtml = (html = '') => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  const parseJsonValue = (value) => {
    if (!value) return null;
    return typeof value === 'string' ? JSON.parse(value) : value;
  };

  const extractPromptHtml = (parsedPayload) => {
    const promptContent = parsedPayload?.prompt;
    if (!promptContent) return null;
    const html = String(promptContent).trim();
    return html || null;
  };

  const TOOL_ACTIVE_FORMATTER = (payload) => {
    const activeLabel = t('config.history.tool_active_label');
    const values = [];
    const pushActive = (value) => value !== undefined && values.push(`${activeLabel}: ${String(value)}`);

    const isRetrieveProducts = payload?.entity_label === 'retrieve_products' || payload?.type === 'retrieve_products';

    if (isRetrieveProducts) {
      pushActive(payload?.active);
      if (!values.length) pushActive(payload?.is_active);
    } else {
      pushActive(payload?.active);
      pushActive(payload?.is_active);
    }

    if (payload?.config?.message) {
      values.push(stripHtml(payload.config.message).trim());
    }

    return values.length ? values.join('\n') : payload?.type || null;
  };

  const CUSTOM_FIELD_FORMATTER = (payload) => {
    const nameLabel = t('config.history.custom_field.name_label');
    const descriptionLabel = t('config.history.custom_field.description_label');
    const nameValue = payload?.name ? `${nameLabel}: ${payload.name}` : null;
    const descText = payload?.description ? stripHtml(payload.description).trim() : null;
    const descriptionValue = descText ? `${descriptionLabel}: ${descText}` : null;
    return [nameValue, descriptionValue].filter(Boolean).join('\n');
  };

  const ASSIGNMENT_FORMATTER = (payload) => {
    const nameLabel = t('config.history.assignment.name_label');
    const conditionLabel = t('config.history.assignment.condition_label');
    const nameValue = payload?.name ? `${nameLabel}: ${payload.name}` : null;
    const conditionText = payload?.condition ? `${conditionLabel}: ${stripHtml(payload.condition).trim()}` : null;
    return [nameValue, conditionText].filter(Boolean).join('\n');
  };

  const SPECIAL_VALUE_HANDLERS = {
    prompt: (payload) => extractPromptHtml(payload),
    flow_intention: (payload) => payload?.name || null,
    agent: (payload) => payload?.name || null,
    custom_field: CUSTOM_FIELD_FORMATTER,
    assignment: ASSIGNMENT_FORMATTER,
    guideline: (payload) => payload?.condition,
    tool: TOOL_ACTIVE_FORMATTER,
    custom_tag: (payload) => {
      const nameLabel = t('config.history.custom_tag.name_label');
      const descriptionLabel = t('config.history.custom_tag.description_label');
      const nameValue = payload?.name ? `${nameLabel}: ${payload.name}` : null;
      const descriptionText = payload?.description ? stripHtml(payload.description).trim() : null;
      const descriptionValue = descriptionText ? `${descriptionLabel}: ${descriptionText}` : null;
      return [nameValue, descriptionValue].filter(Boolean).join('\n');
    }
  };

  const getSpecialValue = (category, parsedPayload) => {
    if (!parsedPayload) return null;
    return SPECIAL_VALUE_HANDLERS[category]?.(parsedPayload) ?? null;
  };

  const getSectionContent = (rawValue, category) => {
    if (!rawValue) return null;
    const parsed = parseJsonValue(rawValue);
    const special = getSpecialValue(category, parsed);
    if (special) return special;
    return formatValue(rawValue);
  };

  const getCategoryWithEntityLabel = (category, entityLabel) => {
    if (category === 'prompt' && entityLabel) {
      const promptKey = `config.history.prompts.${entityLabel}`;
      const promptLabel = t(promptKey);
      return promptLabel === promptKey ? entityLabel : promptLabel;
    }

    if (category === 'tool' && entityLabel) {
      const toolKey = `config.history.tools.${entityLabel}`;
      const toolLabel = t(toolKey);
      return toolLabel === toolKey ? entityLabel : toolLabel;
    }

    return t(`config.history.categories.${category}`);
  };

  const renderSectionContent = (content, category) => {
    if (!content) return null;

    if (category === 'prompt') {
      return (
        <div className="tw-border tw-border-gray-200 tw-rounded-xl tw-bg-gray-50 tw-overflow-auto ql-snow">
          <div
            className="ql-editor tw-text-sm tw-text-gray-950"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </div>
      );
    }

    return (
      <pre className="tw-border tw-border-gray-200 tw-rounded-xl tw-p-4 tw-bg-gray-50 tw-text-sm tw-text-gray-950 tw-overflow-auto tw-whitespace-pre-wrap">
        {content}
      </pre>
    );
  };

  if (!historyRecord) return null;

  const recordToDisplay = fullRecord || historyRecord;
  const nowContent = getSectionContent(recordToDisplay.now, recordToDisplay.category);
  const beforeContent = getSectionContent(recordToDisplay.before, recordToDisplay.category);

  const getCategoryTitle = (category) => t(`config.history.categories.${category}`);

  return (
    <div className="tw-wrapper">
      <RightModalLayout
        title={getCategoryTitle(recordToDisplay.category)}
        closeAction={closeModal}
      >
        <ModalSection
          title={t('config.history.modal.details_title')}
          afterContent={(
            <div>
              <div>
                <span className="tw-text-[12px] tw-font-normal tw-text-blue-500">
                  {t('config.history.modal.date')}:
                </span>
                <span className="tw-font-normal tw-ml-1 tw-text-[13px] tw-text-gray-950">
                  {moment(recordToDisplay.created_at).format('DD/MM/YYYY HH:mm:ss')}
                </span>
              </div>
              <div>
                <span className="tw-text-[12px] tw-font-normal tw-text-blue-500">
                  {t('config.history.modal.category')}:
                </span>
              <span className="tw-font-normal tw-ml-1 tw-text-[13px] tw-text-gray-950">
                  {getCategoryWithEntityLabel(recordToDisplay.category, recordToDisplay.entity_label)}
                </span>
              </div>
              <div>
                <span className="tw-text-[12px] tw-font-normal tw-text-blue-500">
                  {t('config.history.modal.event')}:
                </span>
                <span className="tw-font-normal tw-ml-1 tw-text-[13px] tw-text-gray-950">
                  {translateEvent(recordToDisplay.event)}
                </span>
              </div>
              <div>
                <span className="tw-text-[12px] tw-font-normal tw-text-blue-500">
                  {t('config.history.modal.author')}:
                </span>
                <span className="tw-font-normal tw-ml-1 tw-text-[13px] tw-text-gray-950">
                  {recordToDisplay.author?.full_name || recordToDisplay.author}
                </span>
              </div>
              {recordToDisplay.reason && (
                <div>
                  <span className="tw-text-[12px] tw-font-normal tw-text-blue-500">
                    {t('config.history.modal.reason')}:
                  </span>
                  <span className="tw-font-normal tw-ml-1 tw-text-[13px] tw-text-gray-950">
                    {recordToDisplay.reason}
                  </span>
                </div>
              )}
            </div>
          )}
        />

        {nowContent && (
          <div className="tw-mt-0">
            <ModalSection
              title={t('config.history.modal.now')}
              afterContent={renderSectionContent(nowContent, recordToDisplay.category)}
            />
          </div>
        )}

        {beforeContent && (
          <div className="tw-mt-0">
            <ModalSection
              title={t('config.history.modal.before')}
              afterContent={renderSectionContent(beforeContent, recordToDisplay.category)}
            />
          </div>
        )}
      </RightModalLayout>
    </div>
  );
};

export default HistoryDetailModal;
